import type { TracePreviewBaseDto } from '@terra/shared-contracts-trace';
import type { RunRow, TraceItem } from './signals-content.types';
import {
  getTraceItemCreatedAtIsoString,
  getTraceItemCreatedAtMs,
} from './signal-runs-date.utils';

export type SignalRunItem = {
  id: string;
  name: string;
  agentName: string;
  status: string;
  createdAt: Date;
  threadId?: string;
  originalTrace: TracePreviewBaseDto;
  isAttackExecution: boolean;
};

const NEO_POST_ATTACK_TRACE_NAME = 'neo-post-attack';

const omitNeoPostAttackTraces = <TTrace extends { name: string }>(
  traces: TTrace[],
): TTrace[] =>
  traces.filter((trace) => trace.name !== NEO_POST_ATTACK_TRACE_NAME);

export const mergeSignalRelatedTraces = <TTrace extends { id: string }>({
  traces = [],
  generationTraces = [],
}: {
  traces?: TTrace[];
  generationTraces?: TTrace[];
}): TTrace[] => {
  const linkedTraceIds = new Set(traces.map((trace) => trace.id));

  return [
    ...traces,
    ...generationTraces.filter((trace) => !linkedTraceIds.has(trace.id)),
  ];
};

const pickRepresentativeTrace = (
  traces: TracePreviewBaseDto[],
): TracePreviewBaseDto => {
  const neoTrace = traces.find((trace) => trace.name === 'neo');

  if (neoTrace) {
    return neoTrace;
  }

  return [...traces].sort(
    (left, right) =>
      new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
  )[0] as TracePreviewBaseDto;
};

export const toSignalRunItems = ({
  traces,
  executionLabel,
}: {
  traces: TracePreviewBaseDto[];
  executionLabel: string;
}): SignalRunItem[] => {
  const visibleTraces = omitNeoPostAttackTraces(traces);
  const tracesWithoutExecutionId: TracePreviewBaseDto[] = [];
  const tracesByExecutionId = new Map<string, TracePreviewBaseDto[]>();

  for (const trace of visibleTraces) {
    if (!trace.executionId) {
      tracesWithoutExecutionId.push(trace);
      continue;
    }

    const group = tracesByExecutionId.get(trace.executionId) ?? [];

    group.push(trace);
    tracesByExecutionId.set(trace.executionId, group);
  }

  const executionRuns = [...tracesByExecutionId.entries()].map(
    ([executionId, group]) => {
      const representative = pickRepresentativeTrace(group);

      return {
        id: executionId,
        name: executionLabel,
        agentName: representative.name,
        status: representative.status,
        createdAt: new Date(representative.createdAt),
        threadId: representative.threadId,
        originalTrace: representative,
        isAttackExecution: true,
      };
    },
  );

  const legacyRuns = tracesWithoutExecutionId.map((trace) => ({
    id: trace.id,
    name: trace.name || executionLabel,
    agentName: trace.name,
    status: trace.status,
    createdAt: new Date(trace.createdAt),
    threadId: trace.threadId,
    originalTrace: trace,
    isAttackExecution: false,
  }));

  return [...executionRuns, ...legacyRuns].sort(
    (left, right) => right.createdAt.getTime() - left.createdAt.getTime(),
  );
};

const traceItemToRunRow = ({
  trace,
  rowId,
  isSignalArchived,
}: {
  trace: TraceItem;
  rowId: string;
  isSignalArchived: boolean;
}): RunRow => ({
  id: rowId,
  threadId: trace.threadId,
  agentName: trace.name,
  status: trace.status || 'Unknown',
  ranAt: getTraceItemCreatedAtIsoString(trace),
  isArchived: trace.isArchived === true || isSignalArchived,
});

const compareTraceItemsByCreatedAtAsc = (
  left: TraceItem,
  right: TraceItem,
): number => getTraceItemCreatedAtMs(left) - getTraceItemCreatedAtMs(right);

const sortTraceItemsByCreatedAtAsc = (traces: TraceItem[]): TraceItem[] =>
  [...traces].sort(compareTraceItemsByCreatedAtAsc);

const pickRepresentativeTraceItem = (traces: TraceItem[]): TraceItem => {
  const neoTrace = traces.find((trace) => trace.name === 'neo');

  if (neoTrace) {
    return neoTrace;
  }

  const sortedTraces = sortTraceItemsByCreatedAtAsc(traces);

  return sortedTraces[0] as TraceItem;
};

type TraceRunSource = {
  trace: TraceItem;
  rowId: string;
};

const compareTraceRunSourcesByCreatedAtDesc = (
  left: TraceRunSource,
  right: TraceRunSource,
): number =>
  getTraceItemCreatedAtMs(right.trace) - getTraceItemCreatedAtMs(left.trace);

const sortTraceRunSourcesByCreatedAtDesc = (
  sources: TraceRunSource[],
): TraceRunSource[] => [...sources].sort(compareTraceRunSourcesByCreatedAtDesc);

export const buildSignalsContentRunRows = ({
  traces,
  isSignalArchived,
}: {
  traces: TraceItem[];
  isSignalArchived: boolean;
}): RunRow[] => {
  const visibleTraces = omitNeoPostAttackTraces(traces);
  const tracesWithoutExecutionId: TraceItem[] = [];
  const tracesByExecutionId = new Map<string, TraceItem[]>();

  visibleTraces.forEach((trace) => {
    if (!trace.executionId) {
      tracesWithoutExecutionId.push(trace);

      return;
    }

    const group = tracesByExecutionId.get(trace.executionId) ?? [];

    group.push(trace);
    tracesByExecutionId.set(trace.executionId, group);
  });

  const executionSources: TraceRunSource[] = [
    ...tracesByExecutionId.entries(),
  ].map(([executionId, group]) => ({
    trace: pickRepresentativeTraceItem(group),
    rowId: executionId,
  }));

  const legacySources: TraceRunSource[] = tracesWithoutExecutionId.map(
    (trace) => ({
      trace,
      rowId: trace.id,
    }),
  );

  return sortTraceRunSourcesByCreatedAtDesc([
    ...executionSources,
    ...legacySources,
  ]).map(({ trace, rowId }) =>
    traceItemToRunRow({
      trace,
      rowId,
      isSignalArchived,
    }),
  );
};
